import { Box } from '@mui/material';
import { Button } from '@components/button';
import { IconButton } from '@components/iconButton';
import { Times, PenSolid, FileUpload, EllipsisH } from '@assets/svgs';
import { SubjectivityStatus, SubjectivityStyle } from '@lio/rule-engine';
import { OverflowTooltip } from '@components/tooltip/OverflowTooltip';
import { Tooltip } from '@components/tooltip/Tooltip';
import { MenuButton, MenuItemType } from '@components/menu';
import { ColumnType } from '@models/policy';
import { QuoteType, QuoteTypeLabels } from '@models/quotes';
import { formatDateToTimezone } from '@utils/date';
import { TableGridBaseColDef, TableGridRenderCellParams } from '@components/table/types/TableColumnDef.type';
import type { SubjectivitySearchResponse } from '@lio/api-types';

type ColumnParams = { isInternalUser: boolean; isLioSales: boolean };
type Row = SubjectivitySearchResponse['items'][0];

const renderDataCell = (params: TableGridRenderCellParams, isInternalUser: boolean) => {
  const {
    bypassed,
    bypassReason,
    completedByUser,
    documents,
    handleDelete,
    subjectivityType,
    handleOpenViewFilesModal,
    status,
    handleOpenFormDialog,
  } = params.row;

  if (bypassed) {
    const tooltipContent =
      isInternalUser && completedByUser
        ? `<b>Marked as Complete by:</b> ${completedByUser.firstName} ${completedByUser.lastName}${
            bypassReason ? `\n<b>Reason</b>: ${bypassReason}` : ''
          }`
        : '';

    if (isInternalUser && tooltipContent) {
      return (
        <Tooltip title={tooltipContent} config={{ allowWhiteSpace: true, applyMarkup: true }}>
          <Box component='span' sx={{ color: 'text.secondary' }}>
            Marked as Complete
          </Box>
        </Tooltip>
      );
    }
    return (
      <Box component='span' sx={{ color: 'text.secondary' }}>
        Marked as Complete
      </Box>
    );
  }

  if (subjectivityType?.style === SubjectivityStyle.Form) {
    if (status === SubjectivityStatus.Closed) {
      return (
        <Button variant='text' onClick={handleOpenFormDialog} sx={{ padding: 0 }}>
          View Details
        </Button>
      );
    }
    return '-';
  }

  if (documents.length === 1) {
    const document = documents[0];
    if (!document?.file) {
      return isInternalUser ? 'File Missing' : '-';
    }
    return (
      <Box
        sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Button
          variant='text'
          component='a'
          target='_blank'
          href={document.file.downloadUrl ?? ''}
          disabled={document.file.downloadUrl === undefined}
          onClick={(e) => e.stopPropagation()}
          sx={{
            padding: '0',
            justifyContent: 'flex-start',
            overflow: 'hidden',
            '&.Mui-disabled': { fontWeight: '400', cursor: 'default', color: 'text.subdued' },
          }}
        >
          <OverflowTooltip>{document.file.fileName}</OverflowTooltip>
        </Button>
        {handleDelete && (
          <IconButton
            onClick={handleDelete}
            sx={(theme) => ({
              ...theme.icons.sm,
              padding: '5px',
              marginLeft: '4px',
              color: theme.palette.text.subdued,
            })}
          >
            <Times />
          </IconButton>
        )}
      </Box>
    );
  }

  if (documents.length > 1) {
    return (
      <Button variant='text' onClick={handleOpenViewFilesModal} sx={{ padding: 0 }}>
        View Files
      </Button>
    );
  }

  return '-';
};

const getActionColumn = ({ isInternalUser, isLioSales }: ColumnParams): TableGridBaseColDef<Row> => ({
  field: 'action',
  headerName: '',
  hidden: isInternalUser && !isLioSales,
  flex: 0,
  sortable: false,
  renderCell: (params: TableGridRenderCellParams) => {
    const { isDisabled, handleUploadDocument, handleOpenFormDialog, documents, subjectivityType } = params.row;
    return subjectivityType?.style === SubjectivityStyle.Form ? (
      <Tooltip title='Edit'>
        <IconButton sx={{ alignSelf: 'center' }} disabled={isDisabled} onClick={handleOpenFormDialog}>
          <PenSolid />
        </IconButton>
      </Tooltip>
    ) : (
      <Tooltip title='Upload'>
        <IconButton
          sx={{ alignSelf: 'center' }}
          disabled={isDisabled}
          onClick={() => handleUploadDocument(params.row, documents)}
        >
          <FileUpload />
        </IconButton>
      </Tooltip>
    );
  },
});

const getMenuColumn = ({ isInternalUser, isLioSales }: ColumnParams): TableGridBaseColDef<Row> => ({
  field: 'menu',
  type: ColumnType.Action,
  width: 40,
  valueGetter: () => undefined,
  hidden: !(isInternalUser && !isLioSales),
  sortable: false,
  mobileConfig: { hidden: { xs: true }, actionMenu: true },
  renderCell: (params: TableGridRenderCellParams) => {
    const { isDisabled, handleUploadDocument, handleOpenFormDialog, documents, status, subjectivityType } = params.row;

    if (subjectivityType?.style === SubjectivityStyle.Form) {
      return (
        <Tooltip title='Edit'>
          <IconButton
            sx={{ alignSelf: 'center' }}
            disabled={isDisabled}
            onClick={() => handleOpenFormDialog(params.row)}
          >
            <PenSolid />
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <MenuButton
        key='menu-button'
        icon={<EllipsisH />}
        items={[
          {
            type: MenuItemType.Default,
            text: 'Upload Document(s)',
            disabled: isDisabled,
            handleOnClick: () => handleUploadDocument(params.row, documents),
          },
          {
            type: MenuItemType.Default,
            disabled: status !== SubjectivityStatus.Active,
            text: 'Mark As Complete',
            hidden: subjectivityType?.style !== SubjectivityStyle.Document,
            handleOnClick: () => params.row.handleMarkAsComplete(params.row),
          },
        ]}
      />
    );
  },
});

export const getQuoteSubjectivityColumns = ({
  isInternalUser,
  isLioSales,
}: ColumnParams): TableGridBaseColDef<Row>[] => {
  const columns: (TableGridBaseColDef<Row> & { hidden?: boolean })[] = [
    {
      field: 'label',
      headerName: 'Label',
      flex: 2,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => <OverflowTooltip>{params.row.label ?? '-'}</OverflowTooltip>,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 70,
      sortable: false,
      valueGetter: (_: unknown, row: Row) => row.status ?? '-',
    },
    {
      field: 'dueBy',
      headerName: 'Due Days After Effective Date',
      width: 186,
      sortable: false,
      valueGetter: (_: unknown, row: Row) => row.dueDaysAfterPolicyEffective ?? '-',
    },
    {
      field: 'file',
      headerName: 'Data',
      flex: 2,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => renderDataCell(params, isInternalUser),
    },
    getActionColumn({ isInternalUser, isLioSales }),
    getMenuColumn({ isInternalUser, isLioSales }),
  ];

  return columns.filter((col) => !col.hidden);
};

export const getPolicySubjectivityColumns = ({
  isInternalUser,
  isLioSales,
}: ColumnParams): TableGridBaseColDef<Row>[] => {
  const columns: (TableGridBaseColDef<Row> & { hidden?: boolean })[] = [
    {
      field: 'label',
      headerName: 'Label',
      flex: 1,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => <OverflowTooltip>{params.row.label ?? '-'}</OverflowTooltip>,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0,
      sortable: false,
    },
    {
      field: 'quoteType',
      headerName: 'Transaction Type',
      flex: 1,
      sortable: false,
      valueGetter: (_: unknown, row: Row) =>
        row.quote?.quoteType ? QuoteTypeLabels[row.quote.quoteType as QuoteType] : '-',
    },
    {
      field: 'dueBy',
      headerName: 'Due By',
      flex: 0,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => (
        <OverflowTooltip>{params.row.dueBy ? formatDateToTimezone(params.row.dueBy) : '-'}</OverflowTooltip>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => (
        <OverflowTooltip>{params.row.description ?? '-'}</OverflowTooltip>
      ),
    },
    {
      field: 'file',
      headerName: 'Data',
      flex: 1,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => renderDataCell(params, isInternalUser),
    },
    {
      field: 'completedBy',
      headerName: 'Completed By',
      flex: 0,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => {
        const { completedByUser } = params.row;
        return completedByUser ? (
          <OverflowTooltip>{`${completedByUser.firstName} ${completedByUser.lastName}`}</OverflowTooltip>
        ) : (
          '-'
        );
      },
    },
    getActionColumn({ isInternalUser, isLioSales }),
    getMenuColumn({ isInternalUser, isLioSales }),
  ];

  return columns.filter((col) => !col.hidden);
};
